import React, { useEffect, useState } from 'react';
import * as Icon from 'react-feather';
import { useDispatch } from 'react-redux';
import {
  chatHistoryGet,
  sendChatMsg,
  setActiveChatDetails,
  useActiveChatDetails
} from '../../store/slices/tenantSlices/chatSlice';
import { Button, Input } from 'reactstrap';
import { format } from 'date-fns';
import { socket } from '../../config/Socket';
import { useUserDetails } from '../../store/slices/authSlice';
import AttachmentModal from '../../pages/Chat/AttachmentModal';
import { getFileExtension } from '../../utils/common';
import PreviewModal from '../../pages/Chat/PreviewModal';
import config from '../../config';

const ChatModal = () => {
  const dispatch = useDispatch();

  const user = useUserDetails();
  const activeChatDetails = useActiveChatDetails();

  const [isMinimize, setIsMinimize] = useState(false);
  const [message, setMessage] = useState('');
  const [attachModal, setAttachModal] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [isPreviewUrl, setIsPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSend = async (file = undefined) => {
    if ((file ? false : !message?.trim()) || isLoading) return false;
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('fromUserId', user?.supervisorId);
      formData.append('toUserId', activeChatDetails?.user_id);
      formData.append('message', message);
      formData.append('fromRole', 'supervisor');
      formData.append('toRole', 'agent');
      if (file) {
        formData.append('file', file);
      }

      await dispatch(sendChatMsg(formData)).unwrap();
      fetchChatHistory(activeChatDetails);
      setMessage('');
      setAttachModal(false);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('error', error);
    }
  };

  const fetchChatHistory = async (activeChatDetails) => {
    try {
      const payload = {
        fromUserId: user?.supervisorId,
        toUserId: activeChatDetails?.user_id,
        fromRole: 'supervisor',
        toRole: 'agent'
      };
      const response = await dispatch(chatHistoryGet(payload)).unwrap();
      setChatHistory(response?.data?.reverse() || []);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (activeChatDetails) {
      fetchChatHistory(activeChatDetails);
    }
  }, [activeChatDetails]);

  const getFileName = (path) => {
    const pathnames = path?.split('/');
    return pathnames?.[pathnames.length - 1];
  };

  useEffect(() => {
    const handleChatMessage = (message) => {
      console.log('message------->', message);
      if (message?.fromUserId === activeChatDetails?.user_id) {
        fetchChatHistory({ user_id: message?.fromUserId });
      }
    };

    socket.on('chatMessage', handleChatMessage);

    return () => {
      socket.off('chatMessage', handleChatMessage);
    };
  }, [activeChatDetails]);

  const fileDownload = async (url) => {
    try {
      const response = await fetch(url, {
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;

      const filename = url.split('/').pop();
      link.download = filename || 'download';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div
      className={`shadow-sm ${isMinimize ? 'supervisor-chat-modal-small' : 'supervisor-chat-modal'}`}>
      <div className="chat-app__header d-flex align-items-center justify-content-between">
        <strong className="text-capitalize ms-2">{activeChatDetails?.Username}</strong>
        <div className="d-flex gap-2">
          {isMinimize ? (
            <Icon.Maximize2
              size={15}
              className="cursor-pointer"
              onClick={() => setIsMinimize(!isMinimize)}
            />
          ) : (
            <Icon.Minimize2
              size={15}
              className="cursor-pointer"
              onClick={() => setIsMinimize(!isMinimize)}
            />
          )}
          <Icon.X
            size={15}
            className="cursor-pointer"
            onClick={() => dispatch(setActiveChatDetails(null))}
          />
        </div>
      </div>
      <div className="chat-app__messages flex-grow-1 p-3 overflow-auto">
        {chatHistory.map((msg, idx) => {
          const isSentByCurrentUser = msg.fromUserId === user?.supervisorId;
          const formattedTime = format(new Date(msg.timestamp), 'hh:mm a');
          const extension = getFileExtension(msg?.filePath);
          return (
            <div
              key={msg._id}
              className={`chat-app__message-wrapper ${isSentByCurrentUser ? 'sent' : 'received'}`}>
              <div className={`chat-app__message ${msg?.filePath ? 'chat-app__media' : ''}`}>
                {msg?.filePath ? (
                  <div className="chat-app__message-text">
                    {extension === 'pdf' ? (
                      <div>
                        {msg?.fileName ? msg?.fileName : getFileName(msg?.filePath)}
                        <div className="media-icon">
                          <Icon.FileText size={40} />
                        </div>
                        <div className="w-100 d-flex align-items-center gap-1">
                          <div className="w-50">
                            <Button
                              className="w-100"
                              style={{ fontSize: '12px' }}
                              onClick={() =>
                                setIsPreviewUrl(`${config.BASE_URL}${msg?.filePath}`)
                              }>
                              Open
                            </Button>
                          </div>
                          <div className="w-50">
                            <Button
                              className="w-100"
                              style={{ fontSize: '12px' }}
                              onClick={() => {
                                fileDownload(`${config.BASE_URL}${msg?.filePath}`);
                              }}>
                              Download
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {msg?.fileName ? msg?.fileName : getFileName(msg?.filePath)}
                        <div className="image-wrapper">
                          <img src={`${config.BASE_URL}${msg?.filePath}`} />
                        </div>
                        <div className="w-100 d-flex align-items-center gap-1">
                          <div className="w-50">
                            <Button
                              className="w-100"
                              style={{ fontSize: '12px' }}
                              onClick={() =>
                                setIsPreviewUrl(`${config.BASE_URL}${msg?.filePath}`)
                              }>
                              Open
                            </Button>
                          </div>
                          <div className="w-50">
                            <Button
                              className="w-100"
                              style={{ fontSize: '12px' }}
                              onClick={() => {
                                fileDownload(`${config.BASE_URL}${msg?.filePath}`);
                              }}>
                              Download
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="chat-app__message-text">{msg.message}</div>
                )}
                <div className="chat-app__message-time">{formattedTime}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div
        className={`chat-app__input align-items-center p-2 border-top rounded-0 ${isMinimize ? 'd-none' : 'd-flex'}`}>
        <div className="ms-2 pe-2 text-primary cursor-pointer" onClick={() => setAttachModal(true)}>
          <Icon.Paperclip size={20} />
        </div>
        <Input
          type="text"
          placeholder="Type a message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
        <div className="ms-2 pe-2 text-info" onClick={() => handleSend()}>
          <Icon.Send size={20} />
        </div>
      </div>
      {attachModal && (
        <AttachmentModal
          isOpen={attachModal}
          handleCancel={() => setAttachModal(false)}
          handleSubmit={(file) => handleSend(file)}
          isLoading={isLoading}
        />
      )}
      {isPreviewUrl && (
        <PreviewModal
          isOpen={isPreviewUrl}
          url={isPreviewUrl}
          handleCancel={() => setIsPreviewUrl(null)}
        />
      )}
    </div>
  );
};

export default ChatModal;
